import {
    Injector,
    Type,
} from '@angular/core';
import { Logger } from '@services/logger/abstract.logger';

import { NullableStateManager } from 'main/app-state/generics/state-manager';
import {
    BehaviorSubject,
    Observable,
} from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    map,
    tap,
} from 'rxjs/operators';

import { isPermissionExpressionGranted } from './permission-expression-parser';
import { PermissionsAPI } from './permissions.api';
import { PermissionsService } from './permissions.service';

export class PermissionsManager extends NullableStateManager<string[]> implements PermissionsAPI {
    protected _state = new BehaviorSubject<string[] | null>(null);

    private _permissionsService: PermissionsService;
    private _isFetchPending = false;

    constructor(
        injector: Injector,
    ) {
        super();

        this._permissionsService = injector.get<PermissionsService>(PermissionsService);

        // The Injector doesn't work directly with abstract classes, so manual casting to Type<Logger> has to be done.
        // More details: https://github.com/angular/angular/issues/23611
        this._logStateChanges('Permissions', injector.get<Logger>(Logger as Type<Logger>));
    }

    public fetch() {
        this._isFetchPending = true;
        return this._permissionsService.get()
            .pipe(
                tap(data => {
                    this._isFetchPending = false;
                    this._state.next(data);
                }),
            );
    }

    public clean() {
        return new Observable<null>((subscriber) => {
            this._isFetchPending = false;
            this._state.next(null);
            subscriber.next(null);
            subscriber.complete();
        });
    }

    public isGranted(permissionsExpression?: string): Observable<boolean> {
        return this._state.pipe(
            filter(() => !this._isFetchPending),
            map(permissions => isPermissionExpressionGranted(permissions || [], permissionsExpression)),
            distinctUntilChanged(),
        );
    }
}
